<template>
  <div class="relative flex-1 flex justify-center gap-4.5">
    <!-- 左侧：左键的实时值（4 行：行程点 / 灵敏度 / 振动 / 音效） -->
    <div class="flex flex-col items-end gap-5 h-full pt-12.5">
      <RtOverviewBlock
        :label="t('rt.sections.triggerTravel')"
        :icon="RT_CONFIG_SECTION_META.travel.icon"
        :value="formatValue(leftTrigger)"
        align="right"
        :selected="isBlockSelected('travel', 'left')"
        @click="emit('navigate', { tab: 'travel', side: 'left' })"
      />
      <RtOverviewBlock
        :label="t('rt.sections.rapidTrigger')"
        :icon="RT_CONFIG_SECTION_META.rapidTrigger.icon"
        :value="formatRtLevel('left')"
        align="right"
        :selected="isBlockSelected('sensitivity', 'left')"
        @click="emit('navigate', { tab: 'sensitivity', side: 'left' })"
      />
      <RtOverviewBlock
        :label="t('rt.sections.vibration')"
        :icon="RT_CONFIG_SECTION_META.vibration.icon"
        :value="formatVibrationValue(leftVibration)"
        align="right"
        :selected="isBlockSelected('tactile', 'left')"
        @click="emit('navigate', { tab: 'tactile', side: 'left' })"
      />
      <RtOverviewBlock
        label=""
        :icon="RT_CONFIG_SECTION_META.sound.icon"
        :value="formatSoundName(leftBeep)"
        align="right"
        :selected="isBlockSelected('sound', 'left')"
        @click="emit('navigate', { tab: 'sound', side: 'left' })"
      />
    </div>
    <!-- 中间：鼠标布局（mask 填充基于左右键的行程点 level；trigger 填充基于实时 triggerStatus） -->
    <RtMouseLayoutPanel
      :left-level="leftTrigger"
      :right-level="rightTrigger"
      :left-trigger-value="leftTriggerLevel"
      :right-trigger-value="rightTriggerLevel"
      :selected="selectedSide"
      :active-tab="overviewActiveTab"
      :calibration-state="calibrationState"
      @select="onLayoutSelect"
    />
    <!-- 右侧：右键的实时值（4 行：行程点 / 灵敏度 / 振动 / 音效） -->
    <div class="flex flex-col items-start gap-5 h-full pt-12.5">
      <RtOverviewBlock
        :label="t('rt.sections.triggerTravel')"
        :icon="RT_CONFIG_SECTION_META.travel.icon"
        :value="formatValue(rightTrigger)"
        align="left"
        :selected="isBlockSelected('travel', 'right')"
        @click="emit('navigate', { tab: 'travel', side: 'right' })"
      />
      <RtOverviewBlock
        :label="t('rt.sections.rapidTrigger')"
        :icon="RT_CONFIG_SECTION_META.rapidTrigger.icon"
        :value="formatRtLevel('right')"
        align="left"
        :selected="isBlockSelected('sensitivity', 'right')"
        @click="emit('navigate', { tab: 'sensitivity', side: 'right' })"
      />
      <RtOverviewBlock
        :label="t('rt.sections.vibration')"
        :icon="RT_CONFIG_SECTION_META.vibration.icon"
        :value="formatVibrationValue(rightVibration)"
        align="left"
        :selected="isBlockSelected('tactile', 'right')"
        @click="emit('navigate', { tab: 'tactile', side: 'right' })"
      />
      <RtOverviewBlock
        label=""
        :icon="RT_CONFIG_SECTION_META.sound.icon"
        :value="formatSoundName(rightBeep)"
        align="left"
        :selected="isBlockSelected('sound', 'right')"
        @click="emit('navigate', { tab: 'sound', side: 'right' })"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { RtSide, RtVibrationFeedbackType, TriggerStatus } from '@rapoo/device-sdk'
  import RtOverviewBlock from './RtOverviewBlock.vue'
  import RtMouseLayoutPanel from './RtMouseLayoutPanel.vue'
  import {
    RT_CONFIG_SECTION_META,
    type CalibrationVisualState,
    type RtSelectionMode,
  } from '../../types'

  type SideMap<T> = Partial<Record<RtSide, T>>
  type OverviewTab = 'travel' | 'sensitivity' | 'tactile' | 'sound'

  const { t } = useI18n()

  const props = defineProps<{
    /** 行程点（单值） */
    triggerPress?: SideMap<number>
    /** 灵敏度（单值） */
    rtLevel?: SideMap<number>
    /** RT 快速触发开关状态；关闭时对应侧的 RT 行程显示为 `\`。 */
    rtSwitch?: SideMap<boolean>
    /** 按下和释放共用的振动强度。 */
    vibration?: SideMap<number>
    /** 左右键共用的振动反馈微动类型。 */
    vibrationFeedbackType?: RtVibrationFeedbackType
    /** 按下和释放共用的点击音效。 */
    beep?: SideMap<number>
    /** 当前选中侧；'both' 表示同时编辑左右键 */
    mode?: RtSelectionMode
    /**
     * 当前激活的 Tab（行程点 / 灵敏度 / 触感 / 音效）。
     * 仅 activeTab 与块所属 tab 一致、且 mode 命中该侧（或 'both'）时，块才视为选中。
     */
    activeTab?: OverviewTab | 'config' | 'calibration'
    /** 校准 Tab 中左右按键的遮罩颜色状态。 */
    calibrationState?: SideMap<CalibrationVisualState>
    /**
     * 设备最近一次上报的实时行程状态 (0xB5)。
     * 仅在「按键触发行程」tab 启用轮询时由父组件传入；切走后该值会被父组件清空，
     * 此时布局面板的实时 trigger 填充会回退到 0%（不可见），与「其他 tab 不展示」一致。
     */
    triggerStatus?: TriggerStatus
  }>()

  /** 点击某个概览块时通知父组件跳转对应 Tab，并附带当前侧。 */
  const emit = defineEmits<{
    navigate: [payload: { tab: OverviewTab; side: RtSide }]
    'update:mode': [mode: RtSelectionMode]
  }>()

  /**
   * 把 RtSelectionMode 转成 RtMouseLayoutPanel 的 Selection：
   * - 'both' → 两侧都高亮（不暗化任一侧）
   * - 'left' / 'right' → 仅对应侧高亮，另一侧 opacity-30 暗化
   */
  const selectedSide = computed<'left' | 'right' | 'both'>(() => {
    if (props.mode === 'left' || props.mode === 'right') return props.mode
    return 'both'
  })

  /** 配置页不展示覆盖层；校准页仅展示按状态变色的基础覆盖层。 */
  const overviewActiveTab = computed<OverviewTab | 'calibration' | undefined>(() => {
    const tab = props.activeTab
    return tab === 'travel' ||
      tab === 'sensitivity' ||
      tab === 'tactile' ||
      tab === 'sound' ||
      tab === 'calibration'
      ? tab
      : undefined
  })

  const calibrationState = computed(() => props.calibrationState)

  /**
   * 判断某个 (tab, side) 的概览块是否需要高亮：
   * - activeTab 必须等于该块所属的 tab（行程点 / 灵敏度 / 触感 / 音效），
   *   否则即使侧匹配也不算选中（用户当前控制的是别的功能）；
   * - 当前选中侧（selectedSide）必须包含该块的 side：both 命中两侧，单侧模式只命中对应侧。
   */
  function isBlockSelected(tab: OverviewTab, side: RtSide): boolean {
    if (props.activeTab !== tab) return false
    return selectedSide.value === 'both' || selectedSide.value === side
  }

  /**
   * 点击 RtMouseLayoutPanel 的某侧：把「mode」视为一组可选中的按键集合，点击 = 切换该侧是否在内。
   * 规则：
   * - 两侧都已选（'both'）：点击某侧 = 移除该侧，只保留另一侧。
   * - 单侧已选：点击未选中侧 = 加入该侧，回到 'both'；点击已选中侧 = 不处理（不允许出现「两侧都不选」）。
   */
  function onLayoutSelect(side: RtSide) {
    const current = props.mode ?? 'both'

    if (current === 'both') {
      // 两侧都选 → 点击该侧 = 取消该侧，只留另一侧
      emit('update:mode', side === 'left' ? 'right' : 'left')
      return
    }

    // 单侧选中：点未选中的侧 → 加入，回到 'both'；点已选中的侧 → 不处理，避免出现「双侧全不选」。
    if (current === side) return
    emit('update:mode', 'both')
  }

  // 提取各字段到独立 computed，方便模板使用（同时让 `props` 在 script 中有引用，避免 lint 误报）
  const leftTrigger = computed(() => props.triggerPress?.left)
  const rightTrigger = computed(() => props.triggerPress?.right)
  /**
   * 左右键实时行程级别（0-10），驱动布局面板的 trigger 进度填充。
   * 父组件仅在「按键触发行程」tab 启用 0xB5 轮询；切走后 triggerStatus 被清空，
   * 此处回退到 0，对应布局面板的 trigger 填充渐变顶部到 0%（完全透明），
   * 因此其他 tab 不会展示陈旧的实时位置。
   */
  const leftTriggerLevel = computed(() => props.triggerStatus?.leftTriggerLevel ?? 0)
  const rightTriggerLevel = computed(() => props.triggerStatus?.rightTriggerLevel ?? 0)
  const leftVibration = computed(() => props.vibration?.left)
  const rightVibration = computed(() => props.vibration?.right)
  const leftBeep = computed(() => props.beep?.left)
  const rightBeep = computed(() => props.beep?.right)

  /** 数值字段统一格式化：未拉到值显示 —，概览块不显示单位。 */
  function formatValue(value: number | undefined): string {
    return value === undefined || value === null ? '—' : String(value)
  }

  const vibrationTypeLabels = computed<Record<RtVibrationFeedbackType, string>>(() => ({
    0: t('rt.vibration.types.mechanical'),
    1: t('rt.vibration.types.optical'),
    2: t('rt.vibration.types.electromagnetic'),
  }))

  /** 振动概览同时显示微动类型与强度，关闭（0）时用 `/` 表示强度。 */
  function formatVibrationValue(value: number | undefined): string {
    const type = props.vibrationFeedbackType
    if (type === undefined && value === undefined) return '—'
    const label = type === undefined ? '' : vibrationTypeLabels.value[type]
    const strength = value === 0 ? '\\' : formatValue(value)
    return [label, strength].filter(Boolean).join(' ')
  }

  /** 音效概览展示为「音效N」，与音效选择按钮的命名保持一致。 */
  function formatSoundName(value: number | undefined): string {
    return value === undefined || value === null
      ? '—'
      : t('rt.sound.option', { value: String(value) })
  }

  /** 对应侧快速触发关闭时不展示 RT 行程数值。 */
  function formatRtLevel(side: RtSide): string {
    if (!props.rtSwitch?.[side]) return '\\'
    return formatValue(props.rtLevel?.[side])
  }
</script>
